import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function sortNotesDescending(notes) {
  return (notes || []).slice().sort((a, b) => {
    if (!a?.createdAt || !b?.createdAt) {
      return 0;
    }
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
}

function getErrorMessage(error, fallbackMessage) {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message;

  return message || fallbackMessage;
}

function normalizeCountsMap(counts) {
  if (!counts || typeof counts !== "object") {
    return {};
  }

  return Object.keys(counts).reduce((acc, key) => {
    if (key == null || key === "undefined" || key === "null") {
      return acc;
    }

    acc[key] = counts[key];
    return acc;
  }, {});
}

function areCountsMapsEqual(left, right) {
  const leftKeys = Object.keys(left || {});
  const rightKeys = Object.keys(right || {});

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left[key] === right[key]);
}

export default function useCalibrationNotes({
  getOwnerId,
  getNotes,
  addNote,
  updateNote,
  deleteNote,
  initialCountsByOwnerId = {},
  onNotesChanged,
}) {
  const [open, setOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);

  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [notesError, setNotesError] = useState(null);

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [newNoteText, setNewNoteText] = useState("");
  const [adding, setAdding] = useState(false);

  const normalizedInitialCounts = useMemo(
    () => normalizeCountsMap(initialCountsByOwnerId),
    [initialCountsByOwnerId],
  );

  const [notesCountByOwnerId, setNotesCountByOwnerId] = useState(
    normalizedInitialCounts,
  );

  const loadRequestIdRef = useRef(0);
  const notesRef = useRef([]);

  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);

  useEffect(() => {
    setNotesCountByOwnerId((prev) => {
      if (areCountsMapsEqual(prev, normalizedInitialCounts)) {
        return prev;
      }

      return normalizedInitialCounts;
    });
  }, [normalizedInitialCounts]);

  const resolveOwnerId = useCallback(
    (owner) => {
      if (!owner) {
        return null;
      }
      return getOwnerId(owner);
    },
    [getOwnerId],
  );

  const setOwnerCount = useCallback((ownerId, count) => {
    if (!ownerId) {
      return;
    }

    setNotesCountByOwnerId((prev) => {
      if (prev[ownerId] === count) {
        return prev;
      }

      return {
        ...prev,
        [ownerId]: count,
      };
    });
  }, []);

  const notifyNotesChanged = useCallback(
    async (ownerId, count) => {
      if (!onNotesChanged) {
        return;
      }

      await onNotesChanged(ownerId, count);
    },
    [onNotesChanged],
  );

  const loadNotes = useCallback(
    async (ownerArg = selectedOwner) => {
      const ownerId = resolveOwnerId(ownerArg);
      if (!ownerId) {
        return;
      }

      const requestId = ++loadRequestIdRef.current;
      setLoadingNotes(true);
      setNotesError(null);

      try {
        const { data } = await getNotes(ownerId);
        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        const sortedNotes = sortNotesDescending(data);
        setNotes(sortedNotes);
        setOwnerCount(ownerId, sortedNotes.length);
      } catch (error) {
        if (requestId !== loadRequestIdRef.current) {
          return;
        }

        console.error("Błąd pobierania notatek", error);
        setNotesError(getErrorMessage(error, "Nie udało się pobrać notatek."));
      } finally {
        if (requestId === loadRequestIdRef.current) {
          setLoadingNotes(false);
        }
      }
    },
    [getNotes, resolveOwnerId, selectedOwner, setOwnerCount],
  );

  const openNotesDialog = useCallback(
    async (owner) => {
      const ownerId = resolveOwnerId(owner);
      if (!ownerId) {
        return;
      }

      setSelectedOwner(owner);
      setOpen(true);
      await loadNotes(owner);
    },
    [loadNotes, resolveOwnerId],
  );

  const closeNotesDialog = useCallback(() => {
    loadRequestIdRef.current += 1;
    notesRef.current = [];
    setOpen(false);
    setSelectedOwner(null);
    setNotes([]);
    setLoadingNotes(false);
    setNotesError(null);
    setEditingNoteId(null);
    setEditingText("");
    setSaving(false);
    setDeletingId(null);
    setNewNoteText("");
    setAdding(false);
  }, []);

  const startEdit = useCallback((note) => {
    setEditingNoteId(note.id);
    setEditingText(note.text || "");
    setNotesError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingNoteId(null);
    setEditingText("");
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingNoteId) {
      return;
    }

    const trimmedText = editingText.trim();
    if (!trimmedText) {
      setNotesError("Treść notatki nie może być pusta.");
      return;
    }

    setSaving(true);
    setNotesError(null);

    try {
      const { data, status } = await updateNote(editingNoteId, {
        text: editingText,
      });

      if (status !== 200) {
        setNotesError("Nie udało się zapisać notatki.");
        return;
      }

      setNotes((prev) =>
        prev.map((note) => (note.id === editingNoteId ? data : note)),
      );
      setEditingNoteId(null);
      setEditingText("");
    } catch (error) {
      console.error("Błąd przy zapisie notatki", error);
      setNotesError(getErrorMessage(error, "Nie udało się zapisać notatki."));
    } finally {
      setSaving(false);
    }
  }, [editingNoteId, editingText, updateNote]);

  const handleDeleteNote = useCallback(
    async (noteId) => {
      const ownerId = resolveOwnerId(selectedOwner);
      if (!ownerId) {
        return;
      }

      setDeletingId(noteId);
      setNotesError(null);

      try {
        const { status } = await deleteNote(noteId);

        if (status !== 200 && status !== 204) {
          setNotesError("Nie udało się usunąć notatki.");
          return;
        }

        const updatedNotes = notesRef.current.filter(
          (note) => note.id !== noteId,
        );
        setNotes(updatedNotes);
        setOwnerCount(ownerId, updatedNotes.length);
        await notifyNotesChanged(ownerId, updatedNotes.length);
      } catch (error) {
        console.error("Błąd przy usuwaniu notatki", error);
        setNotesError(getErrorMessage(error, "Nie udało się usunąć notatki."));
      } finally {
        setDeletingId(null);
      }
    },
    [
      deleteNote,
      notifyNotesChanged,
      resolveOwnerId,
      selectedOwner,
      setOwnerCount,
    ],
  );

  const startAdd = useCallback(() => {
    setAdding(true);
    setNewNoteText("");
    setNotesError(null);
  }, []);

  const cancelAdd = useCallback(() => {
    setAdding(false);
    setNewNoteText("");
  }, []);

  const saveNew = useCallback(async () => {
    const ownerId = resolveOwnerId(selectedOwner);
    const trimmedText = newNoteText.trim();

    if (!ownerId) {
      return;
    }

    if (!trimmedText) {
      setNotesError("Treść notatki nie może być pusta.");
      return;
    }

    setSaving(true);
    setNotesError(null);

    try {
      const { data, status } = await addNote(ownerId, {
        text: newNoteText,
      });

      if (status !== 201 && status !== 200) {
        setNotesError("Nie udało się dodać notatki.");
        return;
      }

      const updatedNotes = [data, ...notesRef.current];
      setNotes(updatedNotes);
      setOwnerCount(ownerId, updatedNotes.length);
      setNewNoteText("");
      setAdding(false);
      await notifyNotesChanged(ownerId, updatedNotes.length);
    } catch (error) {
      console.error("Błąd przy dodawaniu notatki", error);
      setNotesError(getErrorMessage(error, "Nie udało się dodać notatki."));
    } finally {
      setSaving(false);
    }
  }, [
    addNote,
    newNoteText,
    notifyNotesChanged,
    resolveOwnerId,
    selectedOwner,
    setOwnerCount,
  ]);

  const getNotesCount = useCallback(
    (owner, fallbackCount = 0) => {
      const ownerId = resolveOwnerId(owner);
      if (!ownerId) {
        return fallbackCount;
      }

      const localCount = notesCountByOwnerId[ownerId];
      return localCount === undefined ? fallbackCount : localCount;
    },
    [notesCountByOwnerId, resolveOwnerId],
  );

  return {
    open,
    selectedOwner,
    notes,
    loadingNotes,
    notesError,
    editingNoteId,
    editingText,
    saving,
    deletingId,
    newNoteText,
    adding,
    notesCountByOwnerId,

    setEditingText,
    setNewNoteText,

    loadNotes,
    openNotesDialog,
    closeNotesDialog,

    startEdit,
    cancelEdit,
    saveEdit,
    handleDeleteNote,

    startAdd,
    cancelAdd,
    saveNew,

    getNotesCount,
    setNotesCountByOwnerId,
  };
}
